import { Eye, Download, Pencil } from "lucide-react";
import {
  quotationPositions,
  quotationServices,
} from "../utils/quotationColumns";
import { useSearchParams } from "react-router-dom";
import CreateQuotationModal from "./CreateQuotationModal";
import QuotationWorkflow from "./devis/QuotationWorkflow";
import {
  readQuoteWorkflow,
  quoteTransition,
} from "../services/quotationWorkflow";
import { Select } from "antd";
import {
  cachedQuotationList,
  loadQuotationList,
} from "../services/quotationList";
import PageHeading from "../components/PageHeading";
import { createListSearch } from "../utils/listSearch";
import React, { useEffect, useState, useRef } from "react";
import axios from "../axios/AxiosInstance";
import { Modal, message } from "antd";
import { generatePDF } from "../utils/generatePDFUtils";
import logo from "../assets/img/img/logopdf.png";
import toulouse from "../assets/img/img/logotoulouse.png";
import brive from "../assets/img/img/logobrive.png";
import gmail from "../assets/img/img/gmail.png";
import phone from "../assets/img/img/phone.png";
import cgv from "../assets/img/img/cgv-client.jpg";
import { lineAmount } from "../utils/quotationPricing";

const money = (value) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    value,
  );
const date = (value) =>
  value && !Number.isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";

export default function DevisPage() {
  const [params] = useSearchParams();
  const requestedQuote = params.get("quote");
  const openedQuote = useRef(null);
  const scope = [
    localStorage.getItem("userId"),
    localStorage.getItem("agencyName"),
    localStorage.getItem("accessToken"),
  ].join(":");
  const workflowScope = `jm-quote-workflow:${localStorage.getItem("userId")}:${localStorage.getItem("agencyName")}`;
  const [workflow, setWorkflow] = useState(() =>
    readQuoteWorkflow(workflowScope),
  );
  const [managed, setManaged] = useState(null);
  const [stateFilter, setStateFilter] = useState("all");
  const [chooseClient, setChooseClient] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [createClient, setCreateClient] = useState(null);
  const [revision, setRevision] = useState(null);
  const [rows, setRows] = useState(() => cachedQuotationList(scope) || []);
  const [reload, setReload] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [opening, setOpening] = useState(false);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview],
  );
  const consult = async (quotation, client, download = false) => {
    if (opening) return;
    setOpening(true);
    try {
      await Promise.all(
        [logo, toulouse, brive, gmail, phone, cgv].map(
          (src) =>
            new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = reject;
              img.src = src;
            }),
        ),
      );
      const url = generatePDF(
        {
          ...quotation,
          client: {
            ...client,
            ...(typeof quotation.client === "object" ? quotation.client : {}),
          },
        },
        true,
        null,
        "url",
      );
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = `Devis_${quotation.quotationNumber ?? "Provisoire"}.pdf`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else
        setPreview({ url, number: quotation.quotationNumber ?? "Provisoire" });
    } catch {
      message.error("Impossible d’ouvrir le PDF de ce devis. Réessayez.");
    } finally {
      setOpening(false);
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    const cached = reload === 0 ? cachedQuotationList(scope) : null;
    if (cached) {
      setRows(cached);
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    setError("");
    setProgress(null);
    loadQuotationList(axios, {
      scope,
      signal: controller.signal,
      onProgress: ({ rows, completed, total }) => {
        setRows(rows);
        setProgress({ completed, total });
      },
    })
      .then((result) => {
        if (controller.signal.aborted || !result) return;
        setRows(result.rows);
        if (result.failures)
          setError(
            `${result.failures} client(s) n’ont pas pu être chargés. Cliquez sur Actualiser pour réessayer.`,
          );
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setError(
            "Impossible de charger les devis. Cliquez sur Actualiser pour réessayer.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [scope, reload]);
  useEffect(() => {
    if (!requestedQuote || openedQuote.current === requestedQuote) return;
    const row = rows.find((row) => row.quotation._id === requestedQuote);
    if (row) {
      openedQuote.current = requestedQuote;
      setManaged(row);
    }
  }, [requestedQuote, rows]);
  const refreshWorkflow = () => setWorkflow(readQuoteWorkflow(workflowScope));
  useEffect(() => {
    const sync = (event) => {
      if (event.key === workflowScope)
        setWorkflow(readQuoteWorkflow(workflowScope));
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [workflowScope]);
  const newQuote = async () => {
    setChooseClient(true);
    setClientLoading(true);
    try {
      const { data } = await axios.get("client/freelance");
      setClients(Array.isArray(data) ? data : []);
    } catch {
      message.error("Impossible de charger les clients.");
    } finally {
      setClientLoading(false);
    }
  };
  const created = async (data) => {
    const previousRevision = revision;
    setCreateClient(null);
    setRevision(null);
    setReload((value) => value + 1);
    if (!previousRevision) return;
    const createdQuote = data?.quotation || data;
    if (!createdQuote?._id) {
      message.info(
        "Nouvelle version créée. La réponse du serveur ne permet pas de relier automatiquement les deux devis.",
      );
      return;
    }
    try {
      const saved = readQuoteWorkflow(workflowScope);
      const author =
        [localStorage.getItem("firstname"), localStorage.getItem("lastname")]
          .filter(Boolean)
          .join(" ") || "Freelance";
      const next = quoteTransition(saved[previousRevision._id], {
        state: "Remplacé",
        author,
        note: `Nouvelle proposition : devis n° ${createdQuote.quotationNumber ?? "Provisoire"}.`,
        revisionId: createdQuote._id,
      });
      const version = {
        state: "Créé",
        previousId: previousRevision._id,
        history: [
          {
            state: "Créé",
            author,
            savedAt: new Date().toISOString(),
            note: `Version négociée du devis n° ${previousRevision.quotationNumber ?? "Provisoire"}.`,
          },
        ],
      };
      localStorage.setItem(
        workflowScope,
        JSON.stringify({
          ...saved,
          [previousRevision._id]: next,
          [createdQuote._id]: version,
        }),
      );
      refreshWorkflow();
    } catch {
      message.warning(
        "Le devis est créé, mais son historique local n’a pas pu être enregistré.",
      );
    }
  };
  const visible = rows
    .filter(
      createListSearch(search, [
        "quotation.quotationNumber",
        "quotation.type",
        "quotation.status",
        "quotation.createdAt",
        "client.companyName",
        "client.siret",
        "client.contactPhone",
        "client.contactEMail",
        "client.town",
      ]),
    )
    .filter(
      ({ quotation }) =>
        stateFilter === "all" ||
        (workflow[quotation._id]?.state ||
          (quotation.isDraft ? "Brouillon" : "Créé")) === stateFilter,
    );

  return (
    <div className="bg-slate-50">
      <PageHeading
        icon="contracts"
        title={<> Devis </>}
        description={<> Retrouvez vos devis par numéro ou par client. </>}
      />
      <button
        onClick={newQuote}
        className="mt-5 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white"
      >
        Nouveau devis
      </button>
      <select
        aria-label="État du devis"
        className="ml-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
        value={stateFilter}
        onChange={(e) => setStateFilter(e.target.value)}
      >
        <option value="all">Tous les états</option>
        {[
          "Brouillon",
          "Créé",
          "Envoyé",
          "En négociation",
          "Signé",
          "Refusé",
          "Remplacé",
        ].map((value) => (
          <option key={value}>{value}</option>
        ))}
      </select>
      <input
        aria-label="Rechercher un devis"
        placeholder="Numéro de devis ou nom du client…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="my-6 w-full max-w-md rounded-lg border border-slate-300 px-4 py-3"
      />
      {error && (
        <p role="alert" className="mb-4 text-red-700">
          {error}
        </p>
      )}
      <button
        disabled={loading}
        onClick={() => setReload((value) => value + 1)}
        className="mb-4 ml-3 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
      >
        Actualiser
      </button>
      {loading && (
        <p role="status" className="mb-4 text-sm text-slate-500">
          Chargement des devis…{" "}
          {progress
            ? `${progress.completed} / ${progress.total} clients vérifiés`
            : ""}
        </p>
      )}
      {(!loading || rows.length > 0) && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                {[
                  "Numéro devis",
                  "Client",
                  "Poste",
                  "Service",
                  "Date",
                  "Validité",
                  "Montant HT",
                  "Suivi",
                  "Actions",
                ].map((label) => (
                  <th key={label} className="px-5 py-4">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map(({ quotation, client }, index) => (
                <tr
                  key={quotation._id || `${client._id}-${index}`}
                  className="hover:bg-slate-50"
                >
                  <td className="px-5 py-4 font-semibold text-blue-800">
                    {quotation.quotationNumber ?? "Provisoire"}
                    {quotation.isDraft && (
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        Brouillon
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-medium">
                    {client.companyName}
                  </td>
                  <td className="px-5 py-4">{quotationPositions(quotation)}</td>
                  <td className="px-5 py-4">{quotationServices(quotation)}</td>
                  <td className="px-5 py-4">{date(quotation.createdAt)}</td>
                  <td className="px-5 py-4">{date(quotation.expiryDate)}</td>
                  <td className="px-5 py-4">
                    {money(
                      (quotation.products || []).reduce(
                        (total, item) => total + lineAmount(item),
                        0,
                      ),
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <button
                      type="button"
                      onClick={() => setManaged({ quotation, client })}
                      title="Gérer le suivi du devis"
                      className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold hover:bg-blue-50"
                    >
                      {workflow[quotation._id]?.state ||
                        (quotation.isDraft ? "Brouillon" : "Créé")}
                    </button>
                    {workflow[quotation._id]?.state && (
                      <p className="mt-1 text-xs text-slate-500">
                        Déclaré sur cet appareil
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        title="Visionner"
                        aria-label="Visionner"
                        type="button"
                        disabled={opening}
                        onClick={() => consult(quotation, client)}
                        className="rounded-lg p-2 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        title="Télécharger"
                        aria-label="Télécharger"
                        type="button"
                        disabled={opening}
                        onClick={() => consult(quotation, client, true)}
                        className="rounded-lg p-2 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        title="Modifier"
                        aria-label="Modifier"
                        type="button"
                        onClick={() => {
                          setRevision(quotation);
                          setCreateClient(client);
                        }}
                        className="rounded-lg p-2 text-blue-700 hover:bg-blue-50"
                      >
                        <Pencil size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!visible.length && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-5 py-10 text-center text-slate-500"
                  >
                    Aucun devis trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        open={chooseClient}
        title="Nouveau devis — choisir le client"
        footer={null}
        onCancel={() => setChooseClient(false)}
      >
        <Select
          loading={clientLoading}
          className="w-full"
          showSearch
          optionFilterProp="label"
          placeholder="Rechercher un client"
          options={clients.map((client) => ({
            value: client._id,
            label: client.companyName,
          }))}
          onChange={(id) => {
            setCreateClient(clients.find((client) => client._id === id));
            setRevision(null);
            setChooseClient(false);
          }}
        />
      </Modal>
      {createClient && (
        <CreateQuotationModal
          key={revision?._id || createClient._id}
          client={createClient}
          initialQuotation={revision}
          isCreateQuotation={true}
          setIsCreateQuotation={(open) => {
            if (!open) {
              setCreateClient(null);
              setRevision(null);
            }
          }}
          onCreated={created}
        />
      )}
      <Modal
        open={Boolean(managed)}
        title={`Suivi du devis n° ${managed?.quotation.quotationNumber ?? "Provisoire"}`}
        width={800}
        footer={null}
        onCancel={() => setManaged(null)}
        destroyOnClose
      >
        {managed && (
          <QuotationWorkflow
            key={managed.quotation._id}
            scope={workflowScope}
            quotation={managed.quotation}
            client={managed.client}
            onChange={refreshWorkflow}
            onDownload={() => consult(managed.quotation, managed.client, true)}
            onRevision={() => {
              setRevision(managed.quotation);
              setCreateClient(managed.client);
              setManaged(null);
            }}
          />
        )}
      </Modal>
      <Modal
        open={Boolean(preview)}
        onCancel={() => setPreview(null)}
        title={`Devis n° ${preview?.number ?? ""}`}
        width={1100}
        centered
        footer={
          preview ? (
            <a
              href={preview.url}
              download={`Devis_${preview.number}.pdf`}
              className="inline-block rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white"
            >
              Télécharger le PDF
            </a>
          ) : null
        }
      >
        {preview && (
          <iframe
            title={`PDF du devis n° ${preview.number}`}
            src={preview.url}
            className="h-[70vh] w-full rounded-lg border border-slate-200"
          />
        )}
      </Modal>
    </div>
  );
}
